<template>
  <div class="container py-5">

    <v-form 
      v-if="!firstLoad"
      :valid.sync="valid" 
      class="columns is-multiline">

      <div class="column is-full flex flex--row flex--justify-space-between">
        <div class="flex flex--row flex--align-start">
          <div class="field">
            <label class="label">Request Status</label>
            <div>{{ requestStatus }}</div>
          </div>
          <div v-if="request.ready_to_be_sent" class="field mx-3">
            <label class="label">Date Sample Ready</label>
            <div>{{ formatDate(parseDate(request.ready_to_be_sent_date)) }}</div>
          </div>
          <div v-if="request.is_shipped" class="field mx-3">
            <label class="label">Date Shipped</label>
            <div>{{ formatDate(parseDate(request.shipped_date)) }}</div>
          </div>
          <div v-if="request.is_shipped" class="field mx-3">
            <label class="label">Expected Return Date</label>
            <div>{{ formatDate(parseDate(request.expected_return_date)) }}</div>
          </div>
          <div v-if="request.is_returned" class="field mx-3">
            <label class="label">Returned Date</label>
            <div>{{ formatDate(parseDate(request.returned_date)) }}</div>
          </div>
          <div v-if="request.is_returned" class="field mx-3">
            <label class="label">Quantity Returned</label>
            <div>{{ request.qty_returned }}</div>
          </div>
        </div>

        <v-switcher 
          v-if="!readyToShip"
          v-model="request.needs_nda"
          @input="update('needs_nda', $event)" 
          label="Request Needs NDA"/>
        
        <v-fade-transition mode="out-in" class="buttons" v-if="readyToShip">
      
          <v-datepicker-modal-slot 
            v-if="readyToShip && !request.ready_to_be_sent"
            @input="update('ready_to_be_sent_date', $event)">
             <button 
              @click.prevent=""
              class="button is-primary">
              Mark as Ready to Ship
            </button>
          </v-datepicker-modal-slot>

          <v-datepicker-modal-slot 
            v-if="request.ready_to_be_sent && !request.is_shipped"
            @input="update('shipped_date', $event)">
            <button
              :disabled="request.ready_to_be_sent && request.sample.qty_available < request.qty_to_send"
              @click.prevent=""
              class="button is-primary">
              Mark as Shipped
            </button>
          </v-datepicker-modal-slot>

          <button
            v-if="request.is_shipped && !request.is_returned" 
            @click.prevent="addingReturnDate = true"
            class="button is-primary">
            Mark as Returned
          </button>

        </v-fade-transition>
      </div>

      <v-fade-transition 
        mode="out-in" 
        class="column is-full py-0 px-0">
        <v-alert 
          v-if="request.ready_to_be_sent && request.sample.qty_available < request.qty_to_send"
          type="danger">
          This sample request is marked as ready to be sent but there is not 
          enough inventory available for this sample. Please adjust the inventory for
          this sample.
        </v-alert>
      </v-fade-transition>
     
      <v-fade-transition 
        mode="out-in" 
        class="column is-full py-0 px-0">
        <v-alert 
          v-if="request.needs_attachment && !request.attachment"
          type="danger">
          This sample request needs an NDA but does not have an NDA attached.
          It cannot be sent out until one is uploaded.
        </v-alert>
      </v-fade-transition>

      <v-fade-transition 
        mode="out-in" 
        class="column is-full py-0 px-0">
        <v-alert 
          v-if="(!request.needs_attachment || !!request.attachment) && !readyToShip"
          type="warning">
          Please complete the sample checklist.
        </v-alert>
      </v-fade-transition>

      <div class="column is-half">

        <div class="menu-label">
          Sample Request Information
        </div>

        <div class="columns is-multiline">
          <div class="column is-half">
            <label class="label">Created On</label>
            <div>{{ formatDate(parseDate(request.created_on)) }}</div>
          </div>
          <div class="column is-half">
            <label class="label">Expected By</label>
            <div>{{ formatDate(parseDate(request.expected_date_needed)) }}</div>
          </div>
        </div>

        <div class="columns" v-if="request.account || request.contact">
          <div class="column" v-if="request.account">
            <label class="label">Account</label>
            <div>{{ request.account.account_name }}</div>
          </div>

          <div class="column" v-if="request.contact">
            <label class="label">Contact</label>
            <div>{{ request.contact.full_name }}</div>
          </div>
        </div>

        <div class="columns">
          <div class="column">
            <label class="label">Request Reason</label>
            <div>{{ request.request_reason }}</div>
          </div>
          <div
            class="column"
            v-if="request.request_reason == 'Project' && request.sample && 
              request.sample.project">
            <label class="label">Project</label>
            <div>{{ request.sample.project.project_name }}</div>
          </div>
        </div>

        <div class="columns">
          <div class="column">
            <label class="label">Requested By</label>
            <div>{{ request.requested_by.display_name }}</div>
          </div>

          <div class="column">
            <label class="label">Requested To*</label>
            <v-select
              :disabled="request.is_returned"
              required
              v-if="users.length > 0"
              :options="users.filter(d => ['office-manager', 'sales-estimator-samples'].includes(d.role))"
              item-text="display_name"
              item-value="id"
              v-model="request.requested_to_id"
              @input="update('requested_to_id', $event)"
              is-small
            />
          </div>
        </div>

        <div class="columns is-multiline pb-3">
          <div class="column is-full py-2">
            <label class="label">Shipping Address*</label>
            <v-text-field
              required
              placeholder="Shipping Address"
              :disabled="request.is_shipped"
              v-model="request.ship_to_address"
              @blur="update('ship_to_address', $event)">
            </v-text-field>
          </div>

          <div class="column is-3 py-2">
            <label class="label">City*</label>
            <v-text-field
              required
              placeholder="Shipping City"
              :disabled="request.is_shipped"
              v-model="request.ship_to_city"
              @blur="update('ship_to_city', $event)">
            </v-text-field>
          </div>

          <div class="column is-3 py-2">
            <label class="label">Province/State*</label>
            <v-text-field
              required
              placeholder="Shipping Province/ State"
              :disabled="request.is_shipped"
              v-model="request.ship_to_province"
              @blur="update('ship_to_province', $event)">
            </v-text-field>
          </div>

          <div class="column is-3 py-2">
            <label class="label">Postal Code*</label>
            <v-text-field
              required
              placeholder="Shipping Postal Code"
              :disabled="request.is_shipped"
              v-model="request.ship_to_postalcode"
              @blur="update('ship_to_postalcode', $event)">
            </v-text-field>
          </div>

          <div class="column is-3 py-2">
            <label class="label">Country*</label>
            <v-text-field
              required
              placeholder="Shipping Country"
              :disabled="request.is_shipped"
              v-model="request.ship_to_country"
              @blur="update('ship_to_country', $event)">
            </v-text-field>
          </div>

          <div class="column is-6">
            <label class="label">Contact Phone Number*</label>
            <v-text-field
              required
              placeholder="Contact Phone Number"
              v-model="request.ship_to_phone"
              @blur="update('ship_to_phone', $event)"
            />
          </div>

          <div class="column is-6">
            <label class="label">Unit Number</label>
            <v-text-field
              placeholder="Shipping Unit Number"
              v-model="request.ship_to_unit_number"
              @blur="update('ship_to_unit_number', $event)"
            />
          </div>
        </div>

        <div class="field" v-if="request.needs_nda">
          <label class="label">Sample Request NDA*</label>
          <div class="mb-2" v-if="request.attachment">
            <a 
              :href="request.attachment"
              target="_blank">
              NDA file
            </a>
          </div>
          <div class="has-text-grey-light mb-2" v-else>
            No NDA Uploaded
          </div>
          <v-drop-zone
            height="120px"
            id='sample-request-nda'
            @input="attach($event)"
            :file-name.sync="upload.filename"
            v-model="upload.file">
          </v-drop-zone>
        </div>

        <div class="field">
          <label class="label">Request Comments</label>
          <div 
            v-if="request.comments" 
            v-html="request.comments">
          </div>
          <div class="has-text-grey-light" v-else>
            No comments for this request
          </div>
        </div>

      </div>

      <div class="column is-half">

        <div class="menu-label">
          Sample Information
        </div>

        <div class="columns is-multiline">
          <div 
            class="column is-one-fifth">
            <label class="label">Product</label>
            <div>{{ request.sample ? request.sample.product_master.name : null }}</div>
          </div>

          <div class="column is-one-fifth">
            <label class="label">Color</label>
            <div>{{ request.sample ? request.sample.color : null }}</div>
          </div>

          <div class="column is-one-fifth">
            <label class="label">Pattern</label>
            <div>{{ request.sample ? request.sample.pattern : null }}</div>
          </div>

          <div class="column is-one-fifth">
            <label class="label">Qty Available</label>
            <v-text-field
              required
              :disabled="request.is_shipped"
              placeholder="Quantity Available"
              is-small
              v-if="request.sample"
              v-model="request.sample.qty_available"
              @blur="updateSample({
                sampleId: request.sample.id,
                data: { qty_available: $event }
              })"/>
          </div> 

          <div class="column is-one-fifth">
            <label class="label">Qty Needed</label>
            <div>{{ request.qty_to_send }}</div>
          </div>  

          <template v-if="request.request_reason == 'Project' && request.sample">
            <div class="column is-one-quarter">
              <label class="label">Type of Glass*</label>
              <div>{{ request.sample ? request.sample.glass_type : null }}</div>
            </div>

            <div class="column is-one-half">
              <label class="label">Power Outcome (W/SQFT)*</label>
              <div>{{ request.sample ? request.sample.power_outcome : null }}</div>
            </div>

            <div class="column is-one-quarter">
              <label class="label">Active or Inactive</label>
              <div class="is-capitalized">
                {{ request.sample ? request.sample.active_inactive : null }}
              </div>
            </div>
          </template>  

          <div 
            v-if="request.sample.image" 
            class="column is-one-third">
            <label class="label">Custom Sample Image</label>
            <img :src="request.sample.image"/>
          </div>
        </div>

        <hr>

        <div class="field">
          <label class="label">Sample Checklist</label>
          <div>
            <v-checkbox 
              v-for="item in checklist"
              :key="item"
              :value="request[item]"
              @input="update(item, $event)"
              :label="item.replaceAll('_', ' ')"
              :disabled="request.ready_to_be_sent"/>
          </div> 
        </div>
        
        <v-fade-transition mode="out-in">
          <div class="field" v-if="request.is_returned">
            <label class="label">Returned Condition</label>
            <v-rich-text 
              v-model="request.returned_condition"
              @blur="update('returned_condition', $event)"/> 
          </div>
        </v-fade-transition>

      </div>
    </v-form>

    <return-date-quantity-modal 
      v-if="!loading"
      v-model="addingReturnDate"
      :sample-request-id="request.id"/>

  </div>
</template>

<script>
// import VTextField from '@/components/base/VTextField'
import VSelect from '@/components/base/VSelect'
import VCheckbox from '@/components/base/VCheckbox'
import VAlert from '@/components/base/VAlert'
import VForm from '@/components/base/VForm'
import VDatepickerModalSlot from '@/components/base/VDatepickerModalSlot'
import VRichText from '@/components/base/VRichText'
import VSwitcher from '@/components/base/VSwitcher'
// import VDatepicker from '@/components/base/VDatepicker'
import VDropZone from '@/components/base/VDropZone'
import VTextField from '@/components/base/VTextField'
import VFadeTransition from '@/components/transitions/VFadeTransition'

import ReturnDateQuantityModal from '@/components/sampleRequests/ReturnDateQuantityModal'

import { parseJSON, format } from 'date-fns'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  components: { 
    VSelect, VForm, VDropZone, VCheckbox, VAlert, VDatepickerModalSlot, 
    VTextField, VRichText, VSwitcher, VFadeTransition, ReturnDateQuantityModal
  },
  data() {
    return {
      valid: false,
      upload: {
        name: null,
        file: null
      },
      checklist: [
        'is_clean', 'is_not_defected', 'is_labelled', 'is_packaged', 
        'has_return_voucher', 'has_marketing_material'
      ],
      parseDate: v => { return v ? parseJSON(v) : v },
      formatDate: v => { return v ? format(v, "yyyy-MM-dd") : v },
      addingReturnDate: false
    }
  },
  computed: {
    ...mapState("sampleRequest", ["request", "loading", "firstLoad"]),
    ...mapState("users", ["users"]),
    ...mapState("auth", ["user"]),
    readyToShip({ request, checklist }) {
      let nNotReady = checklist.map(
        field => request[field]
      ).reduce((a,b) => !b ? a + 1 : a, 0)

      return nNotReady == 0 && (!request.needs_nda || 
        (request.needs_nda && !!request.attachment));
    },
    requestStatus({ request }) {
      if (!request.attachment && request.needs_nda) {
        return 'NDA needed'
      } else if (!request.ready_to_be_sent) {
        return 'Preparing Sample'
      } else if (!request.is_shipped) {
        return 'Ready to Ship'
      } else if (!request.is_returned) {
        return 'Shipped'
      } else if (request.is_returned) {
        return 'Returned'
      } else {
        return 'N/A'
      }
    }
  },
  created() {
    this.initialize();
    window.addEventListener("beforeunload", this.leaving);
  },
  methods: {
    ...mapActions("samples", ["updateSample"]),
    ...mapActions("sampleRequest", [
      "getSampleRequest", "updateSampleRequest", "addAttachment"
    ]),
    ...mapMutations("sampleRequest", [
      "setSampleRequest", "setLoading", "setFirstLoad"
    ]),
    ...mapMutations("app", ["setNotification"]),
    ...mapActions("users", ["getUsers"]),
    initialize() {
      let request = this.$route.params.request;
      if (request) {
        this.setSampleRequest(request);
        this.setLoading(false);
        this.setFirstLoad(false);
      } else {
        this.getSampleRequest(this.$route.params.requestId);
      }

      this.getUsers({ per_page: 100 });
    },
    attach(file) {
      let formData = new FormData();
      formData.append('attachment', file, file.name);
      this.addAttachment({
        sampleRequestId: this.request.id,
        data: formData
      }).then(() => {
        this.setNotification({
          status: true,
          type: 'success',
          message: 'NDA successfully uploaded'
        })
      })
    },
    update(field, value) {
      let data = {};
      data[field] = value;

      this.updateSampleRequest({
        sampleRequestId: this.request.id,
        data: data
      });
    },
    goToList() {
      this.$router.push({ name: 'SampleRequests' });
    },
    leaving() {
      Array.from(document.querySelectorAll('input')).forEach(el => el.blur())
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/flexbox.scss';
</style>